import { useFlashcardSetStudyAnalytics } from "@knowt/syncing/hooks/study/useStudySessionStats";
import StyledTable, { StyledTableColumn } from "@/components/styled/table/StyledTable";
import { SortOrder, SortSettings } from "@/components/styled/table/types";
import {
    useClassStudySessions,
    useFlashcardStudyStates,
    useRawStudySession,
} from "@knowt/syncing/hooks/study/useStudySession";
import { getUsersStudyAnalytics } from "@knowt/syncing/hooks/study/statsUtils";
import { useClassFlashcardStudyStates } from "@knowt/syncing/hooks/study/useStudySession";
import { useMemo, useState } from "react";
import {
    ClassMemberWithDetails,
    ClassSection,
    FlashcardSet,
    ItemType,
    StudySession,
    StudySessionType,
} from "@knowt/syncing/graphql/schema";
import StudentInformationCell from "../../../StudentInformationCell";
import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { themeColors } from "@/utils/themeColors";
import { spacing } from "@/utils/spacing";
import { strokeWidth } from "@/utils/iconProps";
import InputWithSearchbarIcon from "@/components/styled/input/InputWithSearchbarIcon";
import ColumnsPerSelectedStudyMode from "./SetProgressStatsTableColumnsPerStudyMode";
import LazyLoaded from "@/components/LazyLoaded";
import dynamic from "next/dynamic";
import AddStudentToClassSectionState from "../AddStudentToClassSectionState";
import br from "@/styles/breakpoints.module.css";
import { fromEntries } from "@knowt/syncing/utils/genericUtils";

const TestStatsPerUserPopup = dynamic(() => import("../TestStatsPerUserPopup"), {
    ssr: false,
});

export enum ClassMemberSortByKeys {
    NAME = "Name",
    MASTERY = "Mastery",
    LEARN = "Learn",
    PRACTICE = "Practice",
    MATCHING = "Matching",
    SPACED_REPETITION = "Spaced Repetition",
    STARTED = "Started",
    CARDS_SEEN = "Cards Seen",
    TIME_SPENT = "Time Spent",
    LAST_ACTIVE = "Last Active",
    AVERAGE_GRADE = "Average Grade",
    PRACTICE_TEST = "Practice Test",
    LAST_SUBMISSION = "Last Submission",
}

export type MemberSortSettings = {
    sortBy: ClassMemberSortByKeys;
    order: SortOrder;
};

const SetProgressTable = ({
    classId,
    flashcardSet,
    selectedSection,
    singleUserId,
    isTeacherView = true,
}: {
    classId?: string | null;
    flashcardSet?: FlashcardSet | null;
    selectedSection: ClassSection | null;
    singleUserId?: string | null;
    isTeacherView?: boolean;
}) => {
    const [testPopupOpenForUserId, setTestPopupOpenForUserId] = useState<string | null>(null);
    const [studyModeFilter, setStudyModeFilter] = useState<StudySessionType | null>(null);
    const [studentNameQuery, setStudentNameQuery] = useState("");
    const [sortSettings, setSortSettings] = useState<SortSettings | null>({
        sortBy: ClassMemberSortByKeys.NAME,
        order: SortOrder.ASC,
    });

    const { studySessions } = useClassStudySessions({
        classId: classId,
        itemId: flashcardSet?.flashcardSetId,
        isEnabled: !singleUserId,
    });

    const { studySession } = useRawStudySession({
        itemId: flashcardSet?.flashcardSetId || "",
        isEnabled: !!singleUserId,
    });

    const { flashcardStudyStates: flashcardStudyStatesForSingleStudent } = useFlashcardStudyStates({
        flashcardSetId: flashcardSet?.flashcardSetId,
        isEnabled: !!singleUserId,
    });

    const { flashcardStudyStates } = useClassFlashcardStudyStates({
        classId: classId,
        flashcardSetId: flashcardSet?.flashcardSetId,
        isEnabled: !singleUserId,
    });

    const { studentIds, students } = useFlashcardSetStudyAnalytics({
        flashcardSet: flashcardSet,
        selectedSection,
        isEnabled: !singleUserId,
    });

    const studentsObj = useMemo(() => {
        return students?.length
            ? fromEntries(students?.map(student => [student.userId, student]))
            : ({} as Record<string, ClassMemberWithDetails>);
    }, [students]);

    const studentsStudyAnalytics = useMemo(
        () =>
            getUsersStudyAnalytics({
                studySessions: [...(studySessions || []), studySession].filter(Boolean) as StudySession[],
                itemId: flashcardSet?.flashcardSetId,
                itemType: ItemType.FLASHCARDSET,
                flashcardStudyStates: [
                    ...(flashcardStudyStates || []),
                    ...(Object.values(flashcardStudyStatesForSingleStudent || {}) || []),
                ],
                userIds: singleUserId ? [singleUserId] : studentIds,
            }),
        [
            studySessions,
            flashcardSet?.flashcardSetId,
            flashcardStudyStates,
            singleUserId,
            studentIds,
            studySession,
            flashcardStudyStatesForSingleStudent,
        ]
    );

    const filteredMemberUserIds = useMemo(
        () =>
            students
                ?.filter(({ Name, username }) => {
                    const query = studentNameQuery.toLowerCase();

                    return (
                        !studentNameQuery ||
                        Name?.toLowerCase().includes(query) ||
                        username?.toLowerCase().includes(query)
                    );
                })
                .sort((a, b) => {
                    if (!sortSettings || !studentsStudyAnalytics) return 0;

                    const { sortBy, order } = sortSettings;

                    switch (sortBy) {
                        case ClassMemberSortByKeys.NAME:
                            return order === SortOrder.ASC
                                ? a.Name?.localeCompare(b.Name || "") || 0
                                : (b.Name || "").localeCompare(a.Name || "") || 0;
                        case ClassMemberSortByKeys.CARDS_SEEN:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.cardsSeen || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.cardsSeen || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.cardsSeen || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.cardsSeen || 0);
                        case ClassMemberSortByKeys.MASTERY:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.progressPercentage || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.progressPercentage || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.progressPercentage || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.progressPercentage || 0);

                        case ClassMemberSortByKeys.LAST_ACTIVE:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.lastActive || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.lastActive || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.lastActive || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.lastActive || 0);
                        case ClassMemberSortByKeys.TIME_SPENT:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.timeSpent || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.timeSpent || 0);
                        case ClassMemberSortByKeys.AVERAGE_GRADE:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.averageGrade || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.averageGrade || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.averageGrade || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.averageGrade || 0);
                        case ClassMemberSortByKeys.PRACTICE_TEST:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.testHistory.length || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.testHistory.length || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.testHistory.length || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.testHistory.length || 0);
                        case ClassMemberSortByKeys.LAST_SUBMISSION:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.lastSubmission || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.lastSubmission || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.lastSubmission || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.lastSubmission || 0);
                        case ClassMemberSortByKeys.LEARN:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.LEARN.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.LEARN.timeSpent || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.LEARN.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.LEARN.timeSpent || 0);
                        case ClassMemberSortByKeys.PRACTICE:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.timeSpent || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.TEST.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.TEST.timeSpent || 0);
                        case ClassMemberSortByKeys.MATCHING:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.MATCH.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.MATCH.timeSpent || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.MATCH.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.MATCH.timeSpent || 0);
                        case ClassMemberSortByKeys.SPACED_REPETITION:
                            return order === SortOrder.ASC
                                ? (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.SPACED.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.SPACED.timeSpent || 0)
                                : (studentsStudyAnalytics?.[b.userId]?.statsPerMode?.SPACED.timeSpent || 0) -
                                      (studentsStudyAnalytics?.[a.userId]?.statsPerMode?.SPACED.timeSpent || 0);
                        default:
                            return 0;
                    }
                })
                .map(({ userId }) => userId),
        [students, studentsStudyAnalytics, sortSettings, studentNameQuery]
    );

    return (
        <Flex
            style={{
                maxWidth: `calc(100vw - var(--sidebar-collapsed-width) - 3rem)`,
            }}>
            <StyledTable
                rows={singleUserId ? [singleUserId] : filteredMemberUserIds}
                sortSettings={sortSettings}
                onSortChange={newSortSettings => setSortSettings(newSortSettings)}
                disableSelection
                optionsSection={
                    <TableHeaderControlSection
                        searchQuery={studentNameQuery}
                        onSearchQueryChange={setStudentNameQuery}
                        studyMode={studyModeFilter}
                        onStudyModeChange={setStudyModeFilter}
                        showSearch={!singleUserId}
                    />
                }
                customEmptyState={
                    (studentNameQuery && students?.length) || !isTeacherView ? undefined : (
                        <AddStudentToClassSectionState />
                    )
                }
                emptyMessage={studentNameQuery ? "No match for search." : "No study sessions yet."}
                cellProps={{
                    style: {
                        padding: spacing.MD,
                        minWidth:
                            studyModeFilter === StudySessionType.TEST
                                ? "23.33rem"
                                : !studyModeFilter
                                  ? "14rem"
                                  : "17.5rem",
                        maxWidth:
                            studyModeFilter === StudySessionType.TEST
                                ? "23.33rem"
                                : !studyModeFilter
                                  ? "14rem"
                                  : "17.5rem",
                    },
                    className: br.mdDownMinWidthUnset,
                }}
                containerStyle={{
                    maxWidth: "100%",
                }}
                bodyProps={{
                    style: {
                        overflow: "auto",
                    },
                }}>
                {!singleUserId && (
                    <StyledTableColumn
                        title={"Name"}
                        sortKey={ClassMemberSortByKeys.NAME}
                        cellProps={{
                            style: {
                                flex: 1,
                                minWidth: "22rem",
                                maxWidth: "22rem",
                            },
                        }}>
                        {(userId: string) => <StudentInformationCell member={studentsObj?.[userId]} />}
                    </StyledTableColumn>
                )}
                <ColumnsPerSelectedStudyMode
                    usersStudyAnalytics={studentsStudyAnalytics}
                    studyMode={studyModeFilter}
                    onOpenTestPopup={userId => setTestPopupOpenForUserId(userId)}
                    onStudyModeChange={mode => setStudyModeFilter(mode)}
                />
            </StyledTable>

            <LazyLoaded load={!!testPopupOpenForUserId}>
                <TestStatsPerUserPopup
                    isOpen={!!testPopupOpenForUserId}
                    onClose={() => setTestPopupOpenForUserId(null)}
                    member={students?.find(({ userId }) => userId === testPopupOpenForUserId)}
                    testHistory={
                        studentsStudyAnalytics?.[testPopupOpenForUserId || ""]?.statsPerMode?.TEST.testHistory || []
                    }
                />
            </LazyLoaded>
        </Flex>
    );
};

const TableHeaderControlSection = ({
    searchQuery,
    onSearchQueryChange,
    studyMode,
    onStudyModeChange,
    showSearch,
}: {
    searchQuery: string;
    onSearchQueryChange: (query: string) => void;
    studyMode: StudySessionType | null;
    onStudyModeChange: (studyMode: StudySessionType | null) => void;
    showSearch?: boolean;
}) => {
    const studyModesWithLabels = [
        {
            label: "All",
            value: null,
        },
        {
            label: "Learn",
            value: StudySessionType.LEARN,
        },
        {
            label: "Practice Test",
            value: StudySessionType.TEST,
        },
        {
            label: "Matching",
            value: StudySessionType.MATCH,
        },
        {
            label: "Spaced Repetition",
            value: StudySessionType.SPACED,
        },
    ];
    return (
        <FlexColumn
            style={{
                gap: spacing.SM,
                flex: 1,
            }}>
            <span className={"secondaryTextBold1"}>Study Analytics</span>
            <FlexRowAlignCenter
                style={{
                    gap: spacing.XS_2,
                }}>
                {studyModesWithLabels.map(({ label, value }) => (
                    <CircularRectTextButton
                        key={value}
                        className={"secondaryText1"}
                        onClick={() => onStudyModeChange(value)}
                        style={{
                            padding: `${spacing.XS_2} ${spacing.SM}`,
                            backgroundColor: studyMode === value ? themeColors.neutralBlack : themeColors.neutralWhite,
                            color: studyMode === value ? themeColors.neutralWhite : themeColors.neutralBlack,
                            border: studyMode === value ? "none" : `1px solid ${themeColors.neutral1}`,
                        }}>
                        {label}
                    </CircularRectTextButton>
                ))}
                {showSearch && (
                    <InputWithSearchbarIcon
                        value={searchQuery}
                        onChange={e => onSearchQueryChange(e.target.value)}
                        placeholder="Search students"
                        containerStyle={{ alignSelf: "stretch", flex: 1 }}
                        className="secondaryText2"
                        sx={{
                            backgroundColor: themeColors.background,
                            flex: 1,
                            borderRadius: 40,
                            padding: `${spacing.XS_2} ${spacing.SM}`,
                            paddingLeft: spacing.XL,
                            fontSize: "1.2rem",
                            fontWeight: 600,
                            width: "100%",
                            height: "100%",
                            "&:focus": {
                                border: `1px solid ${themeColors.neutral2}`,
                            },
                        }}
                        adornmentProps={{
                            style: { left: spacing.SM, width: "15px", height: "15px" },
                            strokeWidth: strokeWidth.normal,
                            color: themeColors.neutral2,
                        }}
                    />
                )}
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};

export default SetProgressTable;
